import React, { useState, useEffect, useCallback, useMemo } from 'react';
import "./Feed.css";
import { getClips, getEmbeddingFilteredClips, getCategoryFilteredClips, getCategoryAndEmbeddingFilteredClips, incrementMinutesListened, getCategories } from "../../services/HalogenService";
import { useSearchParams } from 'react-router-dom'; // Import useParams
import { SmallClip } from "../../components/SmallClip/SmallClip";
import { CategoryBubbles } from "../../components/CategoryBubbles/CategoryBubbles";
import { pixelsToVh } from '../../helpers/PixelsToViewport';
import { FeedHeader } from './FeedHeader';
import { PLAYBACK_BAR_HEIGHT, EXTENDED_HEADER_HEIGHT, MOBILE_EXTENDED_HEADER_HEIGHT, MOBILE_PLAYBACK_BAR_HEIGHT } from '../../constants';
import { usePostHog } from 'posthog-js/react'
import { useNavigate } from 'react-router-dom';
import { IoClose } from "react-icons/io5";
import { MedFedClip } from '../../components/MedFedClip/MedFedClip';

const NUM_CLIPS = 24;

function Feed() {
    const posthog = usePostHog();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [clips, setClips] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [index, setIndex] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [categories, setCategories] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const windowHeight = window.innerHeight;


    const validSortOptions = ['hot', 'top', 'latest'];
    const initialSortBy = validSortOptions.includes(searchParams.get('sortBy')) ? searchParams.get('sortBy') : 'hot';
    const initialCategories = searchParams.get('categories') ? searchParams.get('categories').split(',') : [];
    const initialQueryTerm = searchParams.get('queryTerm') || '';
    const [sortBy, setSortBy] = useState(initialSortBy);
    const [currentCategoryKeys, setCurrentCategoryKeys] = useState(initialCategories);
    const [queryTerm, setQueryTerm] = useState(initialQueryTerm);

    useEffect(() => {
        setClips([]);
        setIndex(0);
        setHasMore(true);
        fetchClips(0, sortBy, currentCategoryKeys);

        const params = {};
        if (sortBy && sortBy !== '') params.sortBy = sortBy;
        if (currentCategoryKeys.length > 0) params.categories = currentCategoryKeys.join(',');
        if (queryTerm && queryTerm !== '') params.queryTerm = queryTerm;

        // Only call setSearchParams if we have at least one parameter to set
        if (Object.keys(params).length > 0) {
            setSearchParams(params);
        }
    }, [sortBy, queryTerm, currentCategoryKeys]);

    useEffect(() => {
        if (validSortOptions.includes(searchParams.get('sortBy'))) {
            setSortBy(searchParams.get('sortBy'));
        }
    }, [searchParams.get('sortBy')]);

    useEffect(() => {
        const fetchCategories = async () => {
            const currentCategoryKeys = await getCategories();
            setCategories(currentCategoryKeys);
        }

        fetchCategories();
    }, []);

    const selectedCategoryNames = useMemo(() => {
        return categories
            .filter(cat => currentCategoryKeys.includes(cat.key))
            .map(cat => cat.category);
    }, [categories, currentCategoryKeys]);

    const fetchClips = async (startIndex, sortBy, currentCategoryKeys) => {
        posthog.capture('fetched_more_clips', {
            start_index: startIndex,
            sortBy: sortBy,
            query_term: queryTerm
        });
        setIsLoading(true);
        let newClips;
        if (currentCategoryKeys && currentCategoryKeys.length > 0) {
            newClips = queryTerm.trim() === '' ? await getCategoryFilteredClips(NUM_CLIPS, sortBy, startIndex, currentCategoryKeys) : await getCategoryAndEmbeddingFilteredClips(NUM_CLIPS, startIndex, queryTerm, currentCategoryKeys);
        } else {
            newClips = queryTerm.trim() === '' ? await getClips(NUM_CLIPS, sortBy, startIndex) : await getEmbeddingFilteredClips(NUM_CLIPS, startIndex, queryTerm);
        }

        if (newClips.length < NUM_CLIPS) {
            setHasMore(false);
        }

        if (startIndex === 0) {
            setClips(newClips);
        } else {
            setClips(prev => {
                const existingIds = new Set(prev.map(clip => clip.id));
                const uniqueNewClips = newClips.filter(clip => !existingIds.has(clip.id));
                return [...prev, ...uniqueNewClips];
            });
        }

        setIndex(prevIndex => prevIndex + 1);
        setIsLoading(false);
    };

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return (
        <div className="feedContainer">
            <FeedHeader sortBy={sortBy} setSortBy={setSortBy} queryTerm={queryTerm} setQueryTerm={setQueryTerm} categories={categories} currentCategoryKeys={currentCategoryKeys} setCurrentCategoryKeys={setCurrentCategoryKeys} />
                <div className="feedContentContainer" style={{ height: `${100 - pixelsToVh(windowWidth > 768 ? EXTENDED_HEADER_HEIGHT + PLAYBACK_BAR_HEIGHT : MOBILE_EXTENDED_HEADER_HEIGHT + MOBILE_PLAYBACK_BAR_HEIGHT, window.innerHeight)}dvh` }}>
                    {windowWidth > 768 && <div className="categoryBubbleContainer">
                        <CategoryBubbles categories={categories} currentCategoryKeys={currentCategoryKeys} setCurrentCategoryKeys={setCurrentCategoryKeys} />
                    </div>}
                    <div className="filterTextContainer">
                        {queryTerm.trim() !== '' && (
                            <p className="searchFilterText">
                                Search results for: {queryTerm}
                                <span className='clearSearchQueryButton' onClick={() => setQueryTerm('')}>
                                    <IoClose color={'#808080'} />
                                </span>
                            </p>
                        )}
                        {windowWidth <= 768 && currentCategoryKeys.length > 0 && (
                            <p className="categoryFilterText">
                                Selected categories: {selectedCategoryNames.join(', ')}
                                <span className='clearSearchQueryButton' onClick={() => setCurrentCategoryKeys([])}>
                                    <IoClose color={'#808080'} />
                                </span>
                            </p>
                        )}
                    </div>
                    <div className="feed">
                        {clips.length > 0 ? (clips.map((clip, index) => {
                            return (
                                <div key={index} className={`grid-item wide`}>
                                        {(windowWidth > 768 ? <MedFedClip clip={clip} /> : <SmallClip clip={clip} />)}
                                </div>
                            );
                        }))
                        : isLoading ? (
                            <div className="loadingMessage">Loading...</div>
                        ) : (
                            <div className="emptyFeedMessage">No clips found with that search term.</div>
                        )}
                    </div>
                    {hasMore && (
                        <div className='loadMoreContainer' onClick={() => fetchClips(index, sortBy, currentCategoryKeys)}>
                            <div className='line'></div>
                            <button className="loadMore">Load More</button>
                            <div className='line'></div>
                        </div>
                    )}
                </div>
        </div>
    );
}

export default Feed;
